import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { Calendar, MapPin, Tag, Plus, Trash2, Edit3, X, Check, ExternalLink, RefreshCw, Upload, Eye, AlertTriangle, Search, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EVENT_TYPES = ['Expo', 'Demo', 'Campout', 'Live Show', 'Festival', 'Service', 'Meet-Up', 'Workshop', 'AEONrv Meet-Up', 'AEONrv Service'];
const STATUS_OPTIONS = ['published', 'draft', 'canceled'];
const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white focus:outline-none focus:border-orange-500';

const STATUS_STYLES = {
  published: { bg: '#ECFDF5', text: '#059669', label: 'Published' },
  draft: { bg: '#FEF3C7', text: '#D97706', label: 'Draft' },
  canceled: { bg: '#FEE2E2', text: '#DC2626', label: 'Canceled' },
};

const TYPE_COLORS = {
  'Expo': { bg: '#FFF0E8', text: '#E8520A' },
  'Demo': { bg: '#EEF2FF', text: '#4F46E5' },
  'Campout': { bg: '#ECFDF5', text: '#059669' },
  'Live Show': { bg: '#FEF3C7', text: '#D97706' },
  'Festival': { bg: '#FDF2F8', text: '#DB2777' },
  'Service': { bg: '#F0F9FF', text: '#0284C7' },
  'Meet-Up': { bg: '#F5F3FF', text: '#7C3AED' },
  'Workshop': { bg: '#FEF9C3', text: '#A16207' },
  'AEONrv Meet-Up': { bg: '#F5F3FF', text: '#7C3AED' },
  'AEONrv Service': { bg: '#F0F9FF', text: '#0284C7' },
};

const getTypeColor = (type) => TYPE_COLORS[type] || { bg: '#f3f4f6', text: '#6b7280' };

const EventsManagementTab = ({ authHeaders, flash, onUnsavedChanges }) => {
  const [events, setEvents] = useState([]);
  const [serverEvents, setServerEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pageLive, setPageLive] = useState(false);
  const [togglingPage, setTogglingPage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTime, setFilterTime] = useState(''); // '' | 'upcoming' | 'past'
  const changeCount = useRef(0);
  const imageInputRef = useRef(null);
  const [form, setForm] = useState({
    title: '', start_date: '', end_date: '', city: '', state: '', country: 'United States',
    venue: '', description: '', partner: '', event_type: 'Expo', url: '', image: '', status: 'published',
  });

  const markDirty = () => {
    changeCount.current += 1;
    setDirty(true);
  };

  // Notify parent about unsaved changes (for navigation guard)
  useEffect(() => {
    if (onUnsavedChanges) onUnsavedChanges(dirty);
  }, [dirty, onUnsavedChanges]);

  // Browser tab close / refresh guard
  useEffect(() => {
    const handler = (e) => {
      if (dirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const loadEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/portal/admin/events/all`, { headers: authHeaders() });
      setEvents(res.data);
      setServerEvents(JSON.parse(JSON.stringify(res.data)));
    } catch { flash('Failed to load events', true); }
    finally { setLoading(false); }
  }, [flash, authHeaders]);

  useEffect(() => { loadEvents(); loadSyncStatus(); loadPageStatus(); }, [loadEvents]);

  const loadSyncStatus = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/portal/admin/events/sync-status`, { headers: authHeaders() });
      setSyncStatus(res.data);
    } catch { /* ignore */ }
  };

  const loadPageStatus = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/portal/admin/events/page-status`, { headers: authHeaders() });
      setPageLive(res.data.live || false);
    } catch { /* ignore */ }
  };

  const togglePageLive = async () => {
    setTogglingPage(true);
    try {
      const newVal = !pageLive;
      await axios.post(`${BACKEND_URL}/api/portal/admin/events/page-status`, { live: newVal }, { headers: authHeaders() });
      setPageLive(newVal);
      flash(newVal ? 'Events page is now LIVE' : 'Events page is now OFF');
    } catch { flash('Failed to toggle page status', true); }
    finally { setTogglingPage(false); }
  };

  const resetForm = () => {
    setForm({ title: '', start_date: '', end_date: '', city: '', state: '', country: 'United States', venue: '', description: '', partner: '', event_type: 'Expo', url: '', image: '', status: 'published' });
    setEditing(null);
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.start_date) { flash('Title and start date are required', true); return; }
    if (editing) {
      // Update locally
      setEvents(prev => prev.map(e => e.id === editing ? { ...e, ...form } : e));
    } else {
      // Add locally with temp id
      const tempEvent = { ...form, id: `temp-${Date.now()}`, created_at: new Date().toISOString() };
      setEvents(prev => [...prev, tempEvent]);
    }
    markDirty();
    resetForm();
  };

  const handleEdit = (event) => {
    setForm({
      title: event.title || '', start_date: event.start_date || '', end_date: event.end_date || '',
      city: event.city || '', state: event.state || '', country: event.country || 'United States',
      venue: event.venue || '', description: event.description || '', partner: event.partner || '',
      event_type: event.event_type || 'Expo', url: event.url || '', image: event.image || '',
      status: event.status || 'published',
    });
    setEditing(event.id);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this event?')) return;
    setEvents(prev => prev.filter(e => e.id !== id));
    markDirty();
  };

  const handleStatusChange = (id, newStatus) => {
    setEvents(prev => prev.map(e => e.id === id ? { ...e, status: newStatus } : e));
    markDirty();
  };

  // ═══════════════ CONFIRM / DISCARD ═══════════════

  const confirmChanges = async () => {
    setSaving(true);
    const headers = authHeaders();
    try {
      // 1) Delete removed events
      const deletedEvents = serverEvents.filter(se => !events.find(e => e.id === se.id));
      for (const de of deletedEvents) {
        await axios.delete(`${BACKEND_URL}/api/portal/admin/events/${de.id}`, { headers });
      }

      // 2) Create new events (temp IDs)
      for (const event of events) {
        if (event.id.startsWith('temp-')) {
          const { id, created_at, ...data } = event;
          await axios.post(`${BACKEND_URL}/api/portal/admin/events`, data, { headers });
        }
      }

      // 3) Update modified events
      for (const event of events) {
        if (event.id.startsWith('temp-')) continue;
        const server = serverEvents.find(se => se.id === event.id);
        if (!server) continue;
        const changes = {};
        for (const field of ['title', 'start_date', 'end_date', 'city', 'state', 'country', 'venue', 'description', 'partner', 'event_type', 'url', 'image', 'status']) {
          if (event[field] !== server[field]) changes[field] = event[field];
        }
        if (Object.keys(changes).length > 0) {
          await axios.put(`${BACKEND_URL}/api/portal/admin/events/${event.id}`, changes, { headers });
        }
      }

      flash('All changes published successfully');
      changeCount.current = 0;
      setDirty(false);
      await loadEvents();
    } catch {
      flash('Failed to save some changes. Please try again.', true);
      await loadEvents();
    }
    setSaving(false);
  };

  const discardChanges = () => {
    if (!window.confirm('Discard all unsaved changes?')) return;
    setEvents(JSON.parse(JSON.stringify(serverEvents)));
    setEditing(null);
    setShowForm(false);
    changeCount.current = 0;
    setDirty(false);
    flash('Changes discarded');
  };

  // ═══════════════ SYNC & UPLOAD ═══════════════

  const handleSync = async () => {
    if (dirty) {
      if (!window.confirm('You have unsaved changes. Syncing will reload data. Continue?')) return;
    }
    setSyncing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal/admin/events/sync-aeonrv`, {}, { headers: authHeaders() });
      const d = res.data;
      flash(`AEONrv sync: +${d.added} added, ${d.updated} updated, ${d.removed} removed`);
      changeCount.current = 0;
      setDirty(false);
      loadEvents();
      loadSyncStatus();
    } catch { flash('Sync failed', true); }
    finally { setSyncing(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/portal/admin/events/upload-image`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setForm(p => ({ ...p, image: res.data.url }));
      flash('Image uploaded');
    } catch { flash('Upload failed', true); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const now = new Date().toISOString().split('T')[0];

  // Dynamic filter options
  const countries = useMemo(() => [...new Set(events.map(e => e.country).filter(Boolean))].sort(), [events]);
  const types = useMemo(() => [...new Set(events.map(e => e.event_type).filter(Boolean))].sort(), [events]);

  // Filter events
  const filteredEvents = useMemo(() => {
    return events.filter(e => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match = (e.title || '').toLowerCase().includes(q) ||
          (e.partner || '').toLowerCase().includes(q) ||
          (e.city || '').toLowerCase().includes(q) ||
          (e.state || '').toLowerCase().includes(q) ||
          (e.venue || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      if (filterType && e.event_type !== filterType) return false;
      if (filterCountry && e.country !== filterCountry) return false;
      if (filterStatus && (e.status || 'published') !== filterStatus) return false;
      if (filterTime === 'upcoming' && (e.end_date || e.start_date) < now) return false;
      if (filterTime === 'past' && (e.end_date || e.start_date) >= now) return false;
      return true;
    });
  }, [events, searchQuery, filterType, filterCountry, filterStatus, filterTime, now]);

  const hasFilters = searchQuery || filterType || filterCountry || filterStatus || filterTime;
  const clearFilters = () => { setSearchQuery(''); setFilterType(''); setFilterCountry(''); setFilterStatus(''); setFilterTime(''); };

  if (loading) return <div className="flex items-center justify-center py-20 text-gray-400"><RefreshCw size={20} className="animate-spin mr-2" /> Loading events...</div>;

  const publishedCount = events.filter(e => e.status === 'published' || !e.status).length;
  const draftCount = events.filter(e => e.status === 'draft').length;
  const canceledCount = events.filter(e => e.status === 'canceled').length;

  return (
    <div data-testid="events-management-tab">
      {/* Page Live Toggle */}
      <div className={`flex items-center justify-between mb-5 px-4 py-3 rounded-xl border ${pageLive ? 'bg-green-50 border-green-200' : 'bg-gray-100 border-gray-200'}`} data-testid="page-live-toggle">
        <div className="flex items-center gap-3">
          <div className={`w-2.5 h-2.5 rounded-full ${pageLive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
          <div>
            <p className="text-xs font-bold text-gray-900">{pageLive ? 'Events Page is LIVE' : 'Events Page is OFF'}</p>
            <p className="text-[10px] text-gray-500">{pageLive ? 'Visible in menu and accessible to visitors' : 'Hidden from menu and visitors'}</p>
          </div>
        </div>
        <button
          onClick={togglePageLive}
          disabled={togglingPage}
          className={`px-4 py-2 text-xs font-bold rounded-lg transition-all ${
            pageLive
              ? 'bg-red-100 text-red-600 hover:bg-red-200'
              : 'bg-green-600 text-white hover:bg-green-700'
          } disabled:opacity-50`}
          data-testid="toggle-page-btn"
        >
          {togglingPage ? '...' : pageLive ? 'Turn OFF' : 'Turn ON'}
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-gray-900">Events Management</h2>
          <p className="text-sm text-gray-500 mt-1">
            {events.length} total — {publishedCount} published, {draftCount} draft{canceledCount > 0 ? `, ${canceledCount} canceled` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSync}
            disabled={syncing}
            className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-xs font-bold hover:bg-gray-50 transition-all disabled:opacity-50"
            data-testid="sync-aeonrv-btn"
          >
            <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Syncing...' : 'Sync AEONrv'}
          </button>
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white rounded-lg text-xs font-bold hover:bg-gray-800 transition-all"
            data-testid="add-event-btn"
          >
            <Plus size={12} /> Add Event
          </button>
        </div>
      </div>

      {/* Confirm / Discard bar */}
      {dirty && (
        <div className="flex items-center gap-3 mb-4 px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl" data-testid="unsaved-banner">
          <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
          <p className="flex-1 text-xs font-bold text-orange-700">
            {changeCount.current} unsaved change{changeCount.current !== 1 ? 's' : ''} — click <span className="uppercase">Confirm Edit</span> to publish to website
          </p>
          <button
            onClick={discardChanges}
            className="px-3 py-1.5 text-xs font-bold text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="discard-changes-btn"
          >
            Discard
          </button>
          <button
            onClick={confirmChanges}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-bold text-white rounded-lg transition-all"
            style={{ backgroundColor: '#E8520A' }}
            data-testid="confirm-edit-btn"
          >
            {saving ? <RefreshCw size={12} className="animate-spin" /> : <Check size={12} />}
            {saving ? 'Publishing...' : 'Confirm Edit'}
          </button>
        </div>
      )}

      {/* Sync status */}
      {syncStatus && syncStatus.last_sync && (
        <div className="mb-4 px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-2 text-xs text-blue-700" data-testid="sync-status">
          <RefreshCw size={11} />
          Last AEONrv sync: {new Date(syncStatus.last_sync).toLocaleString()} — {syncStatus.total_scraped} scraped, +{syncStatus.added} added, ~{syncStatus.updated} updated, -{syncStatus.removed} removed
          <span className="text-blue-400 ml-auto">Auto: Sundays 8PM PT</span>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 mb-6" data-testid="event-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-gray-900">{editing ? 'Edit Event' : 'New Event'}</h3>
            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
            <input className={inputCls} placeholder="Event Title *" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} data-testid="event-title-input" />
            <input className={inputCls} type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} data-testid="event-start-input" />
            <input className={inputCls} type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} data-testid="event-end-input" />
            <input className={inputCls} placeholder="City" value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} data-testid="event-city-input" />
            <input className={inputCls} placeholder="State / Province" value={form.state} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} data-testid="event-state-input" />
            <input className={inputCls} placeholder="Country" value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))} data-testid="event-country-input" />
            <input className={inputCls} placeholder="Venue" value={form.venue} onChange={e => setForm(p => ({ ...p, venue: e.target.value }))} data-testid="event-venue-input" />
            <input className={inputCls} placeholder="Partner / Company" value={form.partner} onChange={e => setForm(p => ({ ...p, partner: e.target.value }))} data-testid="event-partner-input" />
            <select className={inputCls} value={form.event_type} onChange={e => setForm(p => ({ ...p, event_type: e.target.value }))} data-testid="event-type-input">
              {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input className={inputCls + ' sm:col-span-2 lg:col-span-2'} placeholder="URL (More Info link)" value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} data-testid="event-url-input" />
            <select className={inputCls} value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} data-testid="event-status-input">
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_STYLES[s].label}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <input className={inputCls + ' flex-1'} placeholder="Image URL (or upload →)" value={form.image} onChange={e => setForm(p => ({ ...p, image: e.target.value }))} data-testid="event-image-input" />
            {form.image && (
              <button
                type="button"
                onClick={() => setPreviewImage(form.image)}
                className="flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition-all whitespace-nowrap"
                data-testid="event-image-preview-btn"
              >
                <Eye size={12} />
              </button>
            )}
            <button
              type="button"
              onClick={() => imageInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-bold text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50 whitespace-nowrap"
              data-testid="event-image-upload-btn"
            >
              <Upload size={12} /> {uploading ? '...' : 'Upload'}
            </button>
            <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          </div>
          <textarea className={inputCls + ' mb-3'} rows={2} placeholder="Description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="event-desc-input" />
          <div className="flex gap-2">
            <button onClick={handleSave} className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white rounded-lg transition-all hover:opacity-90" style={{ backgroundColor: '#E8520A' }} data-testid="save-event-btn">
              <Check size={12} /> {editing ? 'Update Event' : 'Create Event'}
            </button>
            <button onClick={resetForm} className="px-4 py-2 text-xs font-bold text-gray-500 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row items-stretch gap-2 mb-3 p-3 bg-gray-50 border border-gray-200 rounded-xl" data-testid="events-filter-bar">
        <div className="relative flex-1">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search events..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-900 bg-white focus:outline-none focus:border-orange-300"
            data-testid="admin-events-search"
          />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-900 bg-white focus:outline-none focus:border-orange-300 cursor-pointer" data-testid="admin-events-type-filter">
          <option value="">All Types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterCountry} onChange={e => setFilterCountry(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-900 bg-white focus:outline-none focus:border-orange-300 cursor-pointer" data-testid="admin-events-country-filter">
          <option value="">All Countries</option>
          {countries.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-900 bg-white focus:outline-none focus:border-orange-300 cursor-pointer" data-testid="admin-events-status-filter">
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_STYLES[s].label}</option>)}
        </select>
        <select value={filterTime} onChange={e => setFilterTime(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-900 bg-white focus:outline-none focus:border-orange-300 cursor-pointer" data-testid="admin-events-time-filter">
          <option value="">All Time</option>
          <option value="upcoming">Upcoming</option>
          <option value="past">Past</option>
        </select>
        {hasFilters && (
          <button onClick={clearFilters} className="px-3 py-2 text-xs font-bold text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all whitespace-nowrap" data-testid="clear-filters-btn">
            <X size={12} className="inline mr-1" />Clear
          </button>
        )}
      </div>

      {/* Color code legend */}
      <div className="flex flex-wrap items-center gap-3 mb-5 px-1" data-testid="type-color-legend">
        {types.map(t => (
          <span key={t} className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">
            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: getTypeColor(t).text }} />
            {t}
          </span>
        ))}
      </div>

      {/* Results count */}
      {hasFilters && (
        <p className="text-xs text-gray-400 mb-3 ml-1">
          <Filter size={10} className="inline mr-1" />
          Showing {filteredEvents.length} of {events.length} events
        </p>
      )}

      {/* Events list — grouped by country, then future/past */}
      <div className="space-y-8">
        {(() => {
          // Group by country alphabetically
          const groups = {};
          filteredEvents.forEach(e => {
            const country = e.country || 'Other';
            if (!groups[country]) groups[country] = [];
            groups[country].push(e);
          });
          const sortedCountries = Object.keys(groups).sort((a, b) => a.localeCompare(b));

          return sortedCountries.map(country => {
            const countryEvents = groups[country];
            const upcoming = countryEvents
              .filter(e => (e.end_date || e.start_date) >= now)
              .sort((a, b) => a.start_date.localeCompare(b.start_date));
            const past = countryEvents
              .filter(e => (e.end_date || e.start_date) < now)
              .sort((a, b) => b.start_date.localeCompare(a.start_date));

            const renderRow = (event) => {
              const isPast = (event.end_date || event.start_date) < now;
              const status = event.status || 'published';
              const statusStyle = STATUS_STYLES[status] || STATUS_STYLES.published;
              return (
                <div
                  key={event.id}
                  className={`border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 transition-all ${
                    status === 'canceled' ? 'border-red-100 bg-red-50/30' :
                    status === 'draft' ? 'border-yellow-100 bg-yellow-50/30' :
                    isPast ? 'border-gray-100 bg-gray-50' : 'border-gray-200 bg-white'
                  }`}
                  data-testid={`event-row-${event.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className={`text-sm font-bold truncate ${status === 'canceled' ? 'text-gray-400 line-through' : isPast ? 'text-gray-400' : 'text-gray-900'}`}>{event.title}</h4>
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: isPast && status === 'published' ? '#f3f4f6' : getTypeColor(event.event_type).bg, color: isPast && status === 'published' ? '#9ca3af' : getTypeColor(event.event_type).text }}>
                        {event.event_type}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}>
                        {statusStyle.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Calendar size={10} /> {event.start_date}{event.end_date && event.end_date !== event.start_date ? ` → ${event.end_date}` : ''}</span>
                      <span className="flex items-center gap-1"><MapPin size={10} /> {event.city}{event.state ? `, ${event.state}` : ''}</span>
                      {event.partner && <span className="flex items-center gap-1"><Tag size={10} /> {event.partner}</span>}
                      {event.url && <a href={event.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-orange-500 hover:underline"><ExternalLink size={10} /> Link</a>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <select
                      value={status}
                      onChange={e => handleStatusChange(event.id, e.target.value)}
                      className="px-2 py-1.5 text-[10px] font-bold border border-gray-200 rounded-lg text-gray-700 bg-white cursor-pointer focus:outline-none"
                      data-testid={`status-select-${event.id}`}
                    >
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{STATUS_STYLES[s].label}</option>)}
                    </select>
                    <button onClick={() => handleEdit(event)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" data-testid={`edit-event-${event.id}`}>
                      <Edit3 size={14} />
                    </button>
                    <button onClick={() => handleDelete(event.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" data-testid={`delete-event-${event.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            };

            return (
              <div key={country}>
                {/* Country header */}
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="text-sm font-black text-gray-900 uppercase tracking-wide">{country}</h3>
                  <div className="flex-1 h-px bg-gray-200" />
                  <span className="text-[10px] font-bold text-gray-400">{countryEvents.length} event{countryEvents.length !== 1 ? 's' : ''}</span>
                </div>

                <div className="space-y-2">
                  {/* Upcoming */}
                  {upcoming.length > 0 && (
                    <>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-green-600 ml-1">Upcoming ({upcoming.length})</p>
                      {upcoming.map(renderRow)}
                    </>
                  )}

                  {/* Past */}
                  {past.length > 0 && (
                    <>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 ml-1 mt-3">Past ({past.length})</p>
                      {past.map(renderRow)}
                    </>
                  )}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-3xl w-full mx-4" onClick={e => e.stopPropagation()}>
            <button onClick={() => setPreviewImage(null)} className="absolute -top-3 -right-3 bg-white rounded-full p-1.5 shadow-lg hover:bg-gray-100 transition-colors z-10">
              <X size={16} className="text-gray-700" />
            </button>
            <img src={previewImage} alt="Event preview" className="w-full max-h-[80vh] object-contain rounded-xl shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsManagementTab;
